import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { ROUTES } from '@/utils/constants';
import { LoadingSpinner } from '@/components/ui';
import type { UserRole } from '@/types/user';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
}

/**
 * Route wrapper that enforces authentication and optional role-based access control.
 *
 * - Redirects unauthenticated users to login page with return path
 * - Checks role permissions when requiredRole is specified
 * - Admin users bypass role checks
 * - Shows loading spinner during authentication state resolution
 *
 * @example
 * <Route
 *   path="/users"
 *   element={
 *     <ProtectedRoute requiredRole="admin">
 *       <UsersPage />
 *     </ProtectedRoute>
 *   }
 * />
 */
export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-phish-bg-0">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the attempted URL for redirect after login
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Check role-based access control (case-insensitive comparison)
  const userRole = user?.role?.toLowerCase();
  if (requiredRole) {
    // platform_admin can only access admin routes, not regular user routes
    if (requiredRole === 'platform_admin') {
      if (userRole !== 'platform_admin') {
        return <Navigate to={ROUTES.DASHBOARD} replace />;
      }
    } else {
      // Regular role check: admin can access all, platform_admin is separate
      if (userRole !== requiredRole && userRole !== 'admin') {
        return <Navigate to={ROUTES.DASHBOARD} replace />;
      }
    }
  }

  return <>{children}</>;
}
